/**
 * Document Title Utilities (Frontend)
 * Handles title formatting and defaults
 */

export interface TitleConfig {
  type: string
  value?: string
}

/**
 * Default title configuration
 */
export const DEFAULT_TITLE_CONFIG: TitleConfig = {
  type: '',
  value: '',
}

/**
 * Get default title for document type
 */
export const getDefaultDocumentTitle = (docType: string): string => {
  const defaults: { [key: string]: string } = {
    consignments: 'Contrato de Consignación',
    estimates: 'Cotización',
    reservations: 'Documento de Reserva',
    salenotes: 'Nota de Venta',
  }

  return defaults[docType] || 'Documento'
}

/**
 * Determines if a document type should show numbering
 * Consignments: NO numbering
 * Others: ALWAYS show numbering
 */
export const shouldShowDocumentNumber = (defaultTitle: string): boolean => {
  // Consignments never show numbers
  return !defaultTitle.toLowerCase().includes('consignación')
}

/**
 * Format document title with automatic numbering
 * - Consignments: Never show number
 * - All others: Always show number (when available)
 */
export const formatDocumentTitle = (
  titleConfig: TitleConfig | undefined,
  defaultTitle: string,
  documentNumber: number | string | undefined
): string => {
  // Use fallback if titleConfig is undefined (backward compatibility)
  const config = titleConfig ?? DEFAULT_TITLE_CONFIG

  // Determine which title to use
  const baseTitle = (config.type === '.' || config.type === 'custom') && config.value ? config.value : defaultTitle

  // Check if this document type should show numbers
  const showNumber = shouldShowDocumentNumber(defaultTitle)

  // Add number if this doc type shows numbers and document has a number
  if (showNumber && documentNumber !== undefined && documentNumber !== 0) {
    return `${baseTitle} ${documentNumber}`
  }

  return baseTitle
}
